import { sortBy } from 'lodash-es';
import {
  type HierarchyOptions,
  type HierarchyItem,
  type ParentOption,
  type ParentOptionsMap,
  type DropdownOption,
} from '#src/types';

declare global {
  interface Window {
    Drupal: {
      t: (key: string) => string;
    };
  }
}

export const ApiTranslations = {
  getHierarchyTexts(
    hierarchy: HierarchyOptions,
    type: 'category' | 'level' | 'fundingSource'
  ) {
    const hierarchyWithTexts: [string, ParentOption][] = Object.entries(
      hierarchy
    )
      .map(
        ([id, item]) =>
          [id, this.getHierarchyItemWithTexts(item, type)] as [
            string,
            ParentOption,
          ]
      )
      .sort((a, b) => {
        const itemA = a[1] as ParentOption;
        const itemB = b[1] as ParentOption;
        const hasOptionsA = itemA.options != null;
        const hasOptionsB = itemB.options != null;
        if (hasOptionsA && !hasOptionsB) return 1;
        if (!hasOptionsA && hasOptionsB) return -1;
        return 0;
      });

    return hierarchyWithTexts.reduce((acc, [id, item]) => {
      acc[id] = item;
      return acc;
    }, {} as ParentOptionsMap);
  },
  getHierarchyItemWithTexts(
    item: HierarchyItem,
    type?: 'category' | 'level' | 'fundingSource'
  ): ParentOption {
    const { id, geoLevelId, contextualDatasetLevelId, options } = item;
    const parentItem = this.getMetadataVocabularyItem(id);

    let optionItems: DropdownOption[] | undefined;
    if (options != null && type != null) {
      optionItems = sortBy(
        options.map((option) => {
          const vocabularyOption = this.getOptionsVocabularyItem(
            option.id,
            type
          );
          return {
            id: option.id,
            name: vocabularyOption?.name ?? option.id,
            geoLevelId,
            contextualDatasetLevelId,
          };
        }),
        'name'
      );
    }

    return {
      id,
      name: parentItem?.name ?? id,
      shortName: parentItem?.shortName ?? id,
      geoLevelId,
      contextualDatasetLevelId,
      options: optionItems,
    };
  },
  getMetadataVocabularyItem(key: string) {
    return window.drupalSettings?.gfx_dashboards?.vocabularies
      ?.dashboards_metadata?.[key];
  },
  getOptionsVocabularyItem(
    key: string,
    type: 'category' | 'level' | 'fundingSource'
  ) {
    const namespaces = {
      category: 'dashboards_categories',
      level: 'dashboards_levels',
      fundingSource: 'dashboards_categories',
    } as const;
    const namespace = namespaces[type];
    return window.drupalSettings?.gfx_dashboards?.vocabularies?.[namespace]?.[
      key
    ];
  },
};
